<template>
  <div class="edit-question-header-container">
    <div class="question-input-section">
      <label class="question-label">Question</label>
      <input
        v-model="questionValue"
        type="text"
        class="question-input"
        :class="{ 'question-input--dirty': isFieldDirty }"
        :placeholder="placeholder"
        @input="handleInput"
        @blur="handleBlur"
        @focus="handleFocus"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Enter a question',
  },
  setFieldDirty: {
    type: Boolean,
    default: false,
  },
  originalValue: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(['update:modelValue', 'input', 'blur', 'focus']);

const questionValue = ref(props.modelValue);
const initialValue = ref('');

onMounted(() => {
  initialValue.value = props.originalValue ?? props.modelValue;
});

watch(() => props.modelValue, (newValue) => {
  questionValue.value = newValue;
});

const originalContent = computed(() => props.originalValue ?? initialValue.value);

const isFieldDirty = computed(() => {
  if (!props.setFieldDirty) return false;
  return questionValue.value !== originalContent.value;
});

const handleInput = (event) => {
  questionValue.value = event.target.value;
  emit('update:modelValue', questionValue.value);
  emit('input', questionValue.value);
};

const handleBlur = () => {
  emit('blur', questionValue.value);
};

const handleFocus = () => {
  emit('focus', questionValue.value);
};
</script>

<style scoped>
.edit-question-header-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 19px 13px;
  background: #FBFBFB;
  border: 1px solid #EAECF0;
}

.edit-question-header {
  width: 100%;
}

.question-input-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.question-label {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 30px;
  color: #475467;
  margin: 0;
}

.question-input {
  width: 100%;
  padding: 10px 14px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  outline: none;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  transition: all 0.2s ease;
}

.question-input::placeholder {
  color: #98a2b3;
}

.question-input:focus {
  border-color: #d6bbfb;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05),
    0 0 0 4px rgba(158, 119, 237, 0.24);
}

.question-input:hover:not(:focus) {
  border-color: #98a2b3;
}

.question-input--dirty {
  background: #F2EDFF;
  border-color: #c4b5fd;
}
</style>

